/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Calendar as CalendarMolecule } from '@ui/molecules/Calendar';
import useTimeZoneContext from '@contexts/useTimeZoneContext';
import { shiftByCalendarDays } from '@utils/calendar-days';
import useModalContext from '@contexts/useModalContext';
import CellHeader from './nested/CellHeader';
import CalendarEvent from './nested/CalendarEvent';
import { CalendarContext } from './CalendarContext';
import useEvents from './hooks/useEvents';
import { useVisitDetails } from './hooks/useVisitDetails';
import { CalendarEventType } from './constants';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function EventWrapper(fcProps) {
    return React.createElement(CalendarEvent, { fcProps: fcProps });
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CellHeaderWrapper(fcProps) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return React.createElement(CellHeader, { fcProps: fcProps });
}
const CalendarComp = React.memo(({ view, onDateRangeChange, calendarOptimizationEnabled, calendarDayMaxEvents, isPrinting, customFilterSection }) => {
    const { openVisitModal, openTimeOffModal, openEditGenericEventModal } = useModalContext();
    const { timezone, timeZonesMapping } = useTimeZoneContext();
    const { actions: { saveVisit, saveGenericEvent }, state: { detailed } } = useContext(CalendarContext);
    // Track the last clicked visit ID to trigger on-demand detail pre-fetching.
    // useVisitDetails fires immediately when pendingVisitId is set, so by the time
    // the VisitEditModal renders it finds the data already in the React Query cache.
    const [pendingVisitId, setPendingVisitId] = useState(null);
    useVisitDetails(pendingVisitId);
    const calendarRef = useRef(null);
    const { fullCalendarEventsCacheRef, rerender } = useEvents();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChangeView = useCallback((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onDateRangeChange(e);
    }, [onDateRangeChange]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEventClick = useCallback((e) => {
        if (e.event.extendedProps.type === CalendarEventType.Visit) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const visitId = e.event.extendedProps.data.id;
            // Trigger on-demand detail pre-fetch: useVisitDetails(pendingVisitId) fires
            // immediately, loading full visit data into the React Query cache before
            // VisitEditModal renders. The modal only receives the ID and fetches its own data.
            setPendingVisitId(visitId);
            openVisitModal(visitId);
        }
        else if (e.event.extendedProps.type === CalendarEventType.Timeoff) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            openTimeOffModal(e.event.extendedProps.data.id);
        }
        else if (e.event.extendedProps.type === CalendarEventType.Generic_event) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            openEditGenericEventModal(e.event.extendedProps.data.id);
        }
    }, [openVisitModal, openTimeOffModal, openEditGenericEventModal, setPendingVisitId]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    const handleEventChange = useCallback((e) => {
        if (e.event.extendedProps.type === CalendarEventType.Visit) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!e.event.start || !e.event.end)
                return;
            const visitData = e.event.extendedProps.data;
            const ianaVisitTz = (timeZonesMapping === null || timeZonesMapping === void 0 ? void 0 : timeZonesMapping[visitData.timeZone]) || visitData.timeZone;
            // Convert the dropped position to the visit's timezone to get the correct local time.
            // Pass the TZ-aware dayjs objects directly so saveVisit's .toISOString() produces
            // the correct UTC timestamp regardless of the user's browser timezone.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const startInVisitTz = dayjs(e.event.start).tz(ianaVisitTz);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const endInVisitTz = dayjs(e.event.end).tz(ianaVisitTz);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            saveVisit(Object.assign(Object.assign({}, visitData), { start: startInVisitTz, end: endInVisitTz }));
        }
        else if (e.event.extendedProps.type === CalendarEventType.Generic_event) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!e.event.start || !e.event.end)
                return;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const eventEnd = e.event.end;
            // Make allDay end date inclusive
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const end = e.event.extendedProps.data.allDay
                ? dayjs(shiftByCalendarDays(eventEnd, -1, timezone))
                : dayjs(eventEnd);
            saveGenericEvent({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                genericEvent: Object.assign(Object.assign({}, e.event.extendedProps.data), { start: dayjs(e.event.start), end })
            });
        }
    }, [timeZonesMapping, timezone, saveVisit, saveGenericEvent]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getEventClassNames = useCallback((arg) => {
        if (arg.event.extendedProps.type === CalendarEventType.Generic_event) {
            return ['--generic-event'];
        }
        return [];
    }, []);
    const memoData = useMemo(() => {
        return [...fullCalendarEventsCacheRef.current];
    }, [rerender]);
    useEffect(() => {
        var _a;
        (_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.requestResize();
    }, [detailed, calendarOptimizationEnabled, isPrinting]);
    return (React.createElement(CalendarMolecule, { schedulerLicenseKey: "0773090554-fcs-1755176084", calendarRef: calendarRef, view: view, events: memoData, eventContent: EventWrapper, dayCellContent: CellHeaderWrapper, eventClassNames: getEventClassNames, onDatesSet: onChangeView, onEventClick: handleEventClick, onEventChange: handleEventChange, dayMaxEvents: calendarOptimizationEnabled ? calendarDayMaxEvents !== null && calendarDayMaxEvents !== void 0 ? calendarDayMaxEvents : 4 : false, moreLinkClick: calendarOptimizationEnabled ? 'popover' : undefined, isPrinting: isPrinting, showNonCurrentDates: false, timeZone: timezone, eventOrder: "start,title", customFilterSection: customFilterSection }));
});
CalendarComp.displayName = 'CalendarComp';
export default CalendarComp;
